import { createContext, useContext, useEffect, useState } from "react";
import { buildAuthUrl } from "../services/auth";
import { usersApi } from "../services/api";
import { DEMO_USER } from "../services/mockData";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user,      setUser]      = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    // Pick up error set by AuthCallback (stored in sessionStorage so it
    // survives the redirect back to /)
    const stored = sessionStorage.getItem("auth_error");
    if (stored) {
      sessionStorage.removeItem("auth_error");
      setAuthError(stored);
    }

    if (localStorage.getItem("demo_mode") === "true") {
      setUser(DEMO_USER);
      setLoading(false);
      return;
    }

    const token = localStorage.getItem("access_token");
    if (token) {
      usersApi.me()
        .then(setUser)
        .catch((err) => {
          // Only a 401 means the session is invalid (the API answers 401 for a bad token AND for an unknown or
          // inactive user; the axios interceptor also clears storage for it). A 500, a 502 from the proxy during a
          // restart or a dropped connection is transient: keep the token so a reload signs the user straight back in
          // instead of forcing the Microsoft login and 2FA again.
          if (err?.response?.status === 401) localStorage.removeItem("access_token");
          else setAuthError("server_unavailable");
        })
        .finally(() => setLoading(false));
      return;
    }

    setLoading(false);
  }, []);

  // Another tab removed the session (its 401 handler, or an explicit logout): sign this tab out too instead of leaving it
  // polling with no token until someone reloads it. `storage` events only fire in the OTHER tabs.
  useEffect(() => {
    const onStorage = (event) => {
      if (localStorage.getItem("demo_mode") === "true") return;
      const sessionCleared = event.key === null || (event.key === "access_token" && !event.newValue);
      if (sessionCleared && !localStorage.getItem("access_token")) window.location.href = "/";
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const login = async () => {
    setAuthError(null);
    // An explicit new login replaces the stored session; keeping the old token would let it authenticate requests
    // (and a late /users/me) while the callback for the NEW account is still being exchanged.
    localStorage.removeItem("access_token");
    try { window.location.href = await buildAuthUrl(); }
    catch { setAuthError("error"); }
  };

  const loginDemo = () => {
    localStorage.setItem("demo_mode", "true");
    setUser(DEMO_USER);
  };

  const logout = () => {
    localStorage.clear();
    sessionStorage.clear();
    setUser(null);
    setAuthError(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginDemo, logout, authError, setUser, setAuthError }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
